import { createHelpers } from "vuex-map-fields";
import tourApi from "@/services/api/tour";
import departureApi from "@/services/api/departure";

const emptySummary = () => ({
  tickets: [],
  discounts: []
});

const getDefaultState = () => ({
  departure: {
    start_date: null,
    end_date: null,
    status: null,
    min_group_size: 0,
    max_group_size: 0,
    public: false,
    price_sync: true,
    prices: {
      gbp: 0,
      eur: 0,
      usd: 0
    },
    created_at: null,
    tour: {
      id: 0,
      title: "",
      type: {
        value: ""
      }
    }
  },
  summary: emptySummary(),
  tickets: [],
  extras: [],
  guides: []
});

const unwrap = payload =>
  payload && typeof payload === "object" && "data" in payload
    ? payload.data
    : payload;

const formatAllocation = allocation => {
  if (allocation === null || allocation === undefined || allocation === "") {
    return "Unlimited";
  }
  const value = Number(allocation);
  return Number.isFinite(value) ? value : "N/A";
};

const normalizeSummary = summary => ({
  tickets: Array.isArray(summary?.tickets) ? summary.tickets : [],
  discounts: Array.isArray(summary?.discounts) ? summary.discounts : []
});

const { getDepartureField, updateDepartureField } = createHelpers({
  getterType: "getDepartureField",
  mutationType: "updateDepartureField"
});

const state = getDefaultState();

const getters = {
  getDepartureField,
  departure: s => s.departure,
  tickets: s => s.tickets,
  extras: s => s.extras,
  guides: s => s.guides,
  summaryTickets: s =>
    Array.isArray(s.summary?.tickets) ? s.summary.tickets : [],
  summaryDiscounts: s =>
    Array.isArray(s.summary?.discounts) ? s.summary.discounts : [],
  isTour: s => s.departure?.tour?.type?.value === "tour",
  totalSold: (s, g) =>
    g.summaryTickets.reduce((sum, ticket) => {
      const value = Number(ticket?.allocated);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0),
  totalAvailableLabel: (s, g) => {
    if (g.summaryTickets.length) {
      const hasUnlimited = g.summaryTickets.some(
        ticket =>
          ticket?.allocation === null ||
          ticket?.allocation === undefined ||
          ticket?.allocation === ""
      );
      if (hasUnlimited) return "Unlimited";

      const total = g.summaryTickets.reduce((sum, ticket) => {
        const value = Number(ticket?.allocation);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);

      if (total > 0) return total;
    }

    const maxGroupSize = Number(s.departure?.max_group_size);
    return Number.isFinite(maxGroupSize) && maxGroupSize > 0
      ? maxGroupSize
      : "N/A";
  },
  ticketRows: (s, g) =>
    g.summaryTickets.map(ticket => {
      const allocated = Number(ticket?.allocated);
      const discounted = Number(ticket?.discounted_allocated);
      const bookingIds = Array.isArray(ticket?.booking_ids)
        ? ticket.booking_ids
        : [];
      return {
        key: ticket?.id ?? ticket?.title ?? "ticket",
        label: ticket?.title || `Ticket ${ticket?.id ?? ""}`.trim(),
        sold: Number.isFinite(allocated) ? allocated : 0,
        discountedLabel: Number.isFinite(discounted) ? discounted : "N/A",
        allocationLabel: formatAllocation(ticket?.allocation),
        bookingIds
      };
    }),
  discountRows: (s, g) =>
    g.summaryDiscounts.map(discount => {
      const label = discount?.code || discount?.description || "No discount";
      const count = Number(
        discount?.bookings_count ?? discount?.count ?? discount?.bookings
      );
      const bookingIds = Array.isArray(discount?.booking_ids)
        ? discount.booking_ids
        : [];
      return {
        key: label,
        label,
        count: Number.isFinite(count) ? count : 0,
        bookingIds
      };
    })
};

const mutations = {
  updateDepartureField,
  setDeparture: (state, departure) => {
    state.departure = departure;
  },
  setSummary: (state, summary) => {
    state.summary = summary;
  },
  setTickets: (state, tickets) => {
    state.tickets = tickets;
  },
  setExtras: (state, extras) => {
    state.extras = extras;
  },
  setGuides: (state, guides) => {
    state.guides = guides;
  }
};

const actions = {
  fetchDeparture: ({ commit }, { tourId, departureId }) => {
    return new Promise((resolve, reject) => {
      tourApi
        .getDeparture(tourId, departureId)
        .then(resp => {
          commit("setDeparture", resp.data);
          resolve(resp.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  updateDeparture: ({ commit }, { tourId, departure }) => {
    return new Promise((resolve, reject) => {
      tourApi
        .updateDeparture(tourId, departure)
        .then(resp => {
          commit("setDeparture", resp.data);
          resolve(resp.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  fetchSummary: async ({ state, commit }, departureId) => {
    const id = departureId ?? state.departure?.id;
    if (!id) {
      commit("setSummary", emptySummary());
      return;
    }
    try {
      const payload = await departureApi.getDepartureSummary(id);
      const data = unwrap(payload);
      commit("setSummary", normalizeSummary(data || {}));
    } catch (err) {
      commit("setSummary", emptySummary());
    }
  },

  fetchTickets: async ({ state, commit }, departureId) => {
    const id = departureId ?? state.departure?.id;
    if (!id) {
      commit("setTickets", []);
      return;
    }
    try {
      const payload = await departureApi.getDepartureTickets(id);
      const data = unwrap(payload);
      const tickets = Array.isArray(data?.tickets)
        ? data.tickets
        : Array.isArray(data)
        ? data
        : [];
      commit("setTickets", tickets);
    } catch (err) {
      commit("setTickets", []);
    }
  },

  fetchExtras: async ({ state, commit }, departure = state.departure) => {
    if (!departure?.id) {
      commit("setExtras", []);
      return;
    }
    try {
      const payload = await departureApi.getExtras(departure);
      const data = unwrap(payload);
      const extras = Array.isArray(data?.extras)
        ? data.extras
        : Array.isArray(data)
        ? data
        : [];
      commit("setExtras", extras);
    } catch (err) {
      commit("setExtras", []);
    }
  },

  fetchGuides: async ({ state, commit }, departure = state.departure) => {
    if (!departure?.id) {
      commit("setGuides", []);
      return;
    }
    try {
      const payload = await departureApi.getGuides(departure);
      const data = unwrap(payload);
      const guides = Array.isArray(data?.guides)
        ? data.guides
        : Array.isArray(data)
        ? data
        : [];
      commit("setGuides", guides);
    } catch (err) {
      commit("setGuides", []);
    }
  },

  assignExtra: async ({ dispatch, state }, { departure, extraId }) => {
    const target = departure ?? state.departure;
    if (!target?.id) return;
    await departureApi.assignExtra(target, { extra_id: extraId });
    await dispatch("fetchExtras", target);
  },

  updateExtra: async ({ dispatch, state }, { departure, extra }) => {
    const target = departure ?? state.departure;
    if (!target?.id) return;
    await departureApi.updateExtra(target, extra);
    await dispatch("fetchExtras", target);
  },

  removeExtra: async ({ dispatch, state }, { departure, extra }) => {
    const target = departure ?? state.departure;
    if (!target?.id) return;
    await departureApi.removeExtra(target, extra);
    await dispatch("fetchExtras", target);
  },

  assignGuide: async ({ dispatch, state }, { departure, guideId }) => {
    const target = departure ?? state.departure;
    if (!target?.id) return;
    await departureApi.assignGuide(target, { guide_id: guideId });
    await dispatch("fetchGuides", target);
  },

  removeGuide: async ({ dispatch, state }, { departure, guide }) => {
    const target = departure ?? state.departure;
    if (!target?.id) return;
    await departureApi.removeGuide(target, guide);
    await dispatch("fetchGuides", target);
  },

  createTicket: async ({ dispatch }, { ticket, departureId }) => {
    await departureApi.createDepartureTicket(ticket);
    const id = departureId ?? ticket?.departure_id;
    await dispatch("fetchTickets", id);
  },

  updateTicket: async ({ dispatch }, { ticket, departureId }) => {
    await departureApi.updateDepartureTicket(ticket);
    const id = departureId ?? ticket?.departure_id;
    await dispatch("fetchTickets", id);
  },

  removeTicket: async ({ dispatch }, { ticket, departureId }) => {
    await departureApi.deleteDepartureTicket(ticket);
    const id = departureId ?? ticket?.departure_id;
    await dispatch("fetchTickets", id);
  },

  resetDeparture: ({ commit }) => {
    const defaultState = getDefaultState();
    commit("setDeparture", defaultState.departure);
    commit("setSummary", defaultState.summary);
    commit("setTickets", defaultState.tickets);
    commit("setExtras", defaultState.extras);
    commit("setGuides", defaultState.guides);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
